import { MoreHorizontal, Pencil, UserX, UserCheck } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@staff-app/ui';
import type { AdminUser } from '@staff-app/shared-types';
import { formatDateMedium } from '@/lib/format-date';
import { safeDistanceToNow } from '@/lib/safe-format';
import { cn } from '@/lib/utils';

interface Props {
  users: AdminUser[];
  currentEmail: string;
  onEditRole: (user: AdminUser) => void;
  onDisable: (user: AdminUser) => void;
  onReenable: (user: AdminUser) => void;
}

function roleBadgeClass(role: string): string {
  switch (role) {
    case 'root':
      return 'bg-destructive/10 text-destructive';
    case 'engineer':
      return 'bg-primary/10 text-primary';
    case 'support':
      return 'bg-info/10 text-info';
    case 'readonly':
      return 'bg-muted text-muted-foreground';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function lastLoginLabel(user: AdminUser): string {
  if (user.disabled) return 'disabled';
  if (!user.lastLoginAt) return 'pending';
  return safeDistanceToNow(
    user.lastLoginAt,
    { addSuffix: true },
    formatDateMedium(user.lastLoginAt),
  );
}

export function AdminUsersTable({ users, currentEmail, onEditRole, onDisable, onReenable }: Props) {
  if (!users.length) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        {'No admin users yet.'}
      </div>
    );
  }

  return (
    <Table data-testid="admin-users-table">
      <TableHeader>
        <TableRow>
          <TableHead>{'Email'}</TableHead>
          <TableHead>{'Role'}</TableHead>
          <TableHead>{'Invited by'}</TableHead>
          <TableHead>{'Last login'}</TableHead>
          <TableHead className="w-[80px] text-right">{'Actions'}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map((u) => {
          const isSelf = u.email === currentEmail;
          return (
            <TableRow
              key={u._id}
              className={cn('hover:bg-accent/50 transition-colors', u.disabled && 'opacity-70')}
              data-testid="admin-user-row"
            >
              <TableCell className="font-medium">
                {u.email}
                {isSelf ? (
                  <span className="ml-2 text-xs text-muted-foreground">({'you'})</span>
                ) : null}
              </TableCell>
              <TableCell>
                <span
                  className={cn(
                    'rounded-sm px-2 py-0.5 text-xs font-semibold uppercase tracking-wide',
                    roleBadgeClass(u.role),
                  )}
                >
                  {u.role}
                </span>
              </TableCell>
              <TableCell className="text-muted-foreground">{u.invitedBy}</TableCell>
              <TableCell className="text-muted-foreground">{lastLoginLabel(u)}</TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-md"
                      aria-label={'Actions'}
                      className="cursor-pointer"
                      data-testid="admin-user-actions"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => onEditRole(u)}
                      data-testid="action-edit-role"
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      {'Change role'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {u.disabled ? (
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => onReenable(u)}
                        data-testid="action-reenable"
                      >
                        <UserCheck className="mr-2 h-4 w-4" />
                        {'Re-enable'}
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        className="cursor-pointer text-destructive focus:text-destructive"
                        onClick={() => onDisable(u)}
                        disabled={isSelf}
                        data-testid="action-disable"
                      >
                        <UserX className="mr-2 h-4 w-4" />
                        {isSelf ? 'Cannot disable self' : 'Disable'}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
